<!-- =========================================================================================
  File Name: CompanyEdit.vue
  Description: Entity Edit Page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/company/pixinvent
========================================================================================== -->

<template>
  <view-page
      :baseRoute='baseRoute'
      :entity='entity'
      :button-access="componentAccess"
      :model-entity="modelEntity"
      :skip-auto-fetch="true"
      :external-model="model"
  >
    <vs-alert class="mb-4" color="danger" title="Entity Not Found" :active.sync="company_not_found">
      <span>Entity record with id: {{ $route.params.id }} not found. </span>
      <span>
        <span>Check </span><router-link :to="{name:'page-company-list'}" class="text-inherit underline">All
        Entities</router-link>
      </span>
    </vs-alert>

    <vs-alert color="warning" title="Deleted Record" v-if="showDeletedWarning" class="mb-4">
      <span>You are viewing the record for a deleted entity</span>
    </vs-alert>

    <div class="vs-row" v-if="data">
      <div class="vs-col w-full mb-base">
        <div class="vs-row">
          <div class="vs-col w-full">
            <company-general-details
                ref="general"
                readonly
                v-model="general_data"
            />
          </div>
          <div class="vs-col w-full">
            <company-profile
                ref="profile"
                readonly
                v-model="profile_data"
                :has-lloyds="hasLloyds"
                :lloyds-syndicate="isLloydsSyndicate"
            />
          </div>
          <div class="vs-col w-full">
            <company-branding
                ref="branding"
                readonly
                v-model="companyLogoFile"
                :current-logo="profile_data.CompanyLogo"
            />
          </div>
          <div class="vs-col w-full">
            <company-external-data
                ref="external"
                readonly
                v-model="external_data"
            />
          </div>
        </div>
      </div>
    </div>

    <template v-slot:extra-button="{ model }">
      <vs-button
          v-if="componentAccess.canPublish.access && model"
          class="mt-2 ml-4"
          color="light"
          @click="openEnableAlert(model)"
      >
        {{ model.IsEnabled ? "Deactivate" : "Activate" }}
      </vs-button>
    </template>
  </view-page>
</template>

<script>

import ViewPage from "@/views/components/Views/ViewPage";
import CompanyGeneralDetails from "../forms/CompanyGeneralDetails";
import CompanyExternalData from "../forms/CompanyExternalData";
import CompanyBranding from '../forms/CompanyBranding';
import CompanyProfile from "../forms/CompanyProfile";
import CompanyValidation from "@/validation/Company";
import NotificationMixin from '@/components/mixins/NotificationMixin';
import EnablePublishMixin from '../../../../components/mixins/EnablePublishMixin';
CompanyValidation.init();

export default {
  components: {
    CompanyGeneralDetails,
    CompanyExternalData,
    CompanyProfile,
    ViewPage,
    CompanyBranding,
  },
  mixins: [NotificationMixin, EnablePublishMixin],
  data () {
    return {
      baseRoute: 'company',
      entity: 'Company',
      data: null,
      model: {}, // Required for EnablePublishMixin
      company_not_found: false,
      modelEntity: this.$api.companies,
      profile_data: {},
      general_data: {},
      companyLogoFile: null,
      external_data: {
        external_links: {},
        webservice_parameters: {},
        webServices: [],
      },
      componentAccess: {
        canEdit: {
          route: 'company.edit',
          access: false,
        },
        canDelete: {
          route: 'company.delete',
          access: false,
        },
        canRestore: {
          route: 'company.restore',
          access: false,
        },
        canPublish: {
          route: 'company.publish',
          access: false,
        },
      },
      forms: [
        'general',
        'profile',
        'external',
        'branding',
      ]
    }
  },
  computed: {
    hasLloyds() {
      return !!(this.general_data.CompanyType_LloydsStatus_FK && this.general_data.CompanyType_LloydsStatus_FK.id)
    },
    isLloydsSyndicate() {
      let id = parseInt(this.general_data.CompanyType_LloydsStatus_FK);

      if (!id
        && this.general_data.CompanyType_LloydsStatus_FK
        && this.general_data.CompanyType_LloydsStatus_FK.id
      ) {
        id = parseInt(this.general_data.CompanyType_LloydsStatus_FK.id);
      }

      // ID for Lloyds Syndicate
      return (id === 17) ? true : false;
    },
    showDeletedWarning() {
      if (!this.data) {
        return false;
      }
      return Boolean(this.data.IsDeleted);
    }
  },
  async mounted() {
    await this.fetch(this.$route.params.id);
  },
  methods: {
    async fetch(companyId) {
      try {
        const { data } = await this.$api.companies.get(companyId);
        this.data = data.data;
        this.model = data.data; // Set model for EnablePublishMixin (and ViewPage via externalModel prop)

        let companyTypeName = 'N/A';

        if (this.data.company_type && this.data.company_type.CompanyTypeName) {
          companyTypeName = this.data.company_type.CompanyTypeName;
        }

        this.general_data = {
          id: this.data.id,
          CompanyName: this.data.CompanyName,
          CompanyCode: this.data.CompanyCode,
          CompanyGroup_FK: this.data.CompanyGroup_FK,
          CompanyType_Entity_FK: this.data.CompanyType_Entity_FK,
          CompanyLogo: this.data.CompanyLogo,
          CompanyAncestor_FK: this.data.CompanyAncestor_FK,
          ParentCompany_FK: this.data.ParentCompany_FK,
          SecondaryParentCompany_FK: this.data.SecondaryParentCompany_FK,
          CompanyExtension: this.data.CompanyExtension,
          CompanyType_LloydsStatus_FK: this.data.CompanyType_LloydsStatus_FK,
          SalesCountryType_FK: this.data.SalesCountryType_FK,
          ProducerStatusType_FK: this.data.ProducerStatusType_FK,
          email_addresses: this.data.email_addresses,
          IsEnabled: this.data.IsEnabled,
          CompanyTypeName: companyTypeName,
          IsInsurer: this.data.IsInsurer,
          IsCoverholder: this.data.IsCoverholder,
          IsProducer: this.data.IsProducer,
          IsOther: this.data.IsOther,
          IsSubProducer: this.data.IsSubProducer,
          Contact_UnderwriterExecutive_FK: this.data.Contact_UnderwriterExecutive_FK,
          Contact_AreaManager_FK: this.data.Contact_AreaManager_FK,
          Contact_PlacingBroker_FK: this.data.Contact_PlacingBroker_FK,
        };

        this.profile_data = this.data.profile;
        this.profile_data.id = this.data.profile.CompanyProfile_PK;

        this.external_data = {
          id: this.data.id,
          external_links: this.data.external_links,
          webservice_parameters: this.data.webservice_parameters,
          webServices: [],
        }
        await this.$store.dispatch('updateIsDeleted', data.data.IsDeleted);
        await this.$store.dispatch('updatePageTitle', this.data.CompanyName);
      } catch (e) {
        console.error(e)
      } finally {
        if (!this.data) {
          this.company_not_found = true
        }
      }
    }
  },
}

</script>
